import { Calendar } from "primereact/calendar";
import { useEffect, useMemo, useRef, useState } from "react";
import { configProps, ViewNames } from "../types";
import { formatDateRange, parseDateRange } from "../helpers/date";
import { useSearchParams } from "react-router";

type DateFilterProps<TView extends ViewNames> = {
  config: configProps<TView>;
};

type DatePickerValue = (Date | null)[] | null;

export default function DateFilter<TView extends ViewNames>({
  config,
}: DateFilterProps<TView>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDateRange, setSelectedDateRange] =
    useState<DatePickerValue>(null);
  const calendarRef = useRef<Calendar>(null);

  const dateFields: string[] = useMemo(() => {
    return config.columns
      .filter((column) => column.inputType === "calendar")
      .map((column) => column.field);
  }, [config.columns]);

  // Synchronize the selectedDateRange state with the URL
  useEffect(() => {
    // Parse the date range from the URL
    const getDateRangeFromUrl = () => {
      const dateRange = searchParams.get(dateFields[0]);
      if (!dateRange) return null;

      const [from, to] = parseDateRange(dateRange);
      return [from || null, to || null] as DatePickerValue;
    };
    const initialRange = getDateRangeFromUrl();
    setSelectedDateRange(initialRange);
  }, [searchParams, dateFields]);

  function handleDatesPicked() {
    if (!selectedDateRange) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (Array.isArray(dateFields)) {
          dateFields.forEach((field: string) => params.delete(field));
        }
        return params;
      });
      return;
    }

    const [fromRaw, toRaw] = selectedDateRange;
    if (!fromRaw) return; // avoid incomplete selection

    const from = new Date(fromRaw);
    from.setHours(0, 0, 0, 0); // start of day

    const to = new Date(toRaw ?? fromRaw);
    to.setHours(23, 59, 59, 999); // end of day

    const formattedDateRange = formatDateRange(from, to);

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (Array.isArray(dateFields)) {
        dateFields.forEach((field: string) => {
          if (formattedDateRange) {
            params.set(field, formattedDateRange);
          } else {
            params.delete(field);
          }
        });
      }
      return params;
    });
  }

  return (
    <Calendar
      ref={calendarRef}
      value={selectedDateRange}
      showIcon
      placeholder="Filtruj według daty"
      dateFormat="d.mm.yy"
      selectionMode="range"
      hideOnRangeSelection
      showButtonBar={true}
      onChange={(e) => {
        const range = e.value as DatePickerValue;
        setSelectedDateRange(range);
      }}
      onTodayButtonClick={() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayRange: DatePickerValue = [today, today];

        setSelectedDateRange(todayRange);
        calendarRef.current?.hide();
      }}
      onHide={handleDatesPicked}
    />
  );
}
